import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Paper, 
  Divider, 
  List, 
  ListItem,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  AppBar,
  Toolbar,
  Grid,
  Menu,
  MenuItem,
  Tabs,
  Tab,
  Avatar
} from '@mui/material';
import { useNavigate, Link } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import ArchiveIcon from '@mui/icons-material/Archive';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import RefreshIcon from '@mui/icons-material/Refresh';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SendIcon from '@mui/icons-material/Send';
import DraftsIcon from '@mui/icons-material/Drafts';
import InboxIcon from '@mui/icons-material/Inbox';
import AddIcon from '@mui/icons-material/Add';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnread';

// Mock data for emails
const mockEmails = [
  {
    id: 1,
    sender: 'Wanjiku Kamau',
    email: 'wanjiku.kamau@example.com',
    subject: 'Property Inquiry - Downtown Loft',
    body: 'Hello,\n\nI am interested in the downtown loft property and would like to schedule a viewing this weekend if possible. I have a few questions about the amenities and parking situation.\n\nCould you please let me know what times are available on Saturday or Sunday?\n\nThank you,\nWanjiku Kamau',
    date: 'Mar 28, 2025',
    time: '09:45 AM',
    read: false,
    starred: false,
    folder: 'inbox'
  },
  {
    id: 2,
    sender: 'Otieno Odhiambo',
    email: 'otieno.o@example.com',
    subject: 'Maintenance Request Follow-up',
    body: 'Hello Lantena Property Management,\n\nI wanted to follow up on the maintenance request I submitted last week regarding the leaking faucet in my bathroom. It\'s getting worse and now there\'s water pooling on the floor.\n\nCould you please provide an update on when a technician might be able to come by?\n\nThank you,\nOtieno Odhiambo\nUnit 302, Riverside Apartments',
    date: 'Mar 27, 2025',
    time: '03:22 PM',
    read: true,
    starred: true,
    folder: 'inbox'
  },
  {
    id: 3,
    sender: 'Njeri Mwangi',
    email: 'njeri.m@example.com',
    subject: 'Rental Application Status',
    body: 'Dear Lantena Team,\n\nI submitted my rental application for the Oakwood property last Tuesday and was wondering about the status. The listing mentioned that the review process typically takes 3-5 business days.\n\nCould you please let me know if you need any additional information from me or when I might expect to hear a decision?\n\nBest regards,\nNjeri Mwangi',
    date: 'Mar 26, 2025',
    time: '11:15 AM',
    read: true,
    starred: false,
    folder: 'inbox'
  },
  {
    id: 4,
    sender: 'Kipchoge Kiptoo',
    email: 'kipchoge.k@example.com',
    subject: 'Lease Renewal - 123 Main St',
    body: 'Dear Mr. Kiptoo,\n\nThis is a reminder that your lease at 123 Main St will be expiring in 60 days. We would like to offer you the opportunity to renew your lease.\n\nPlease let us know if you would like to schedule a time to discuss renewal terms.\n\nBest regards,\nLantena Property Management Team',
    date: 'Mar 25, 2025',
    time: '02:30 PM',
    read: true,
    starred: false,
    folder: 'sent'
  },
  {
    id: 5,
    sender: 'Akinyi Onyango',
    email: 'akinyi.o@example.com',
    subject: 'Your Recent Maintenance Request - Confirmation',
    body: 'Dear Ms. Onyango,\n\nWe have received your maintenance request regarding the leaking faucet in your bathroom. Your request has been assigned to our maintenance team, and a technician will contact you within 24-48 hours to schedule a visit.\n\nReference Number: MR-2025-0327\n\nThank you for your patience.\n\nBest regards,\nLantena Property Management Team',
    date: 'Mar 25, 2025',
    time: '10:15 AM',
    read: true,
    starred: false,
    folder: 'sent'
  },
  {
    id: 6,
    sender: 'Kimani Waweru',
    email: 'kimani.waweru@example.com',
    subject: 'Monthly Property Report - Draft',
    body: 'Monthly property performance report for March 2025.\n\nKey metrics:\n- Occupancy Rate: 94%\n- Rent Collection: 97%\n- Maintenance Requests: 12\n- New Leases: 3\n\nPlease review and provide feedback before sending to property owners.',
    date: 'Mar 24, 2025',
    time: '04:45 PM',
    read: true,
    starred: true,
    folder: 'drafts'
  }
];

const AdminEmails = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [emails, setEmails] = useState(mockEmails);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [composeMode, setComposeMode] = useState(false);
  const [newEmail, setNewEmail] = useState({
    to: '',
    subject: '',
    body: ''
  });
  
  // Folder tabs mapping
  const tabFolders = ['inbox', 'sent', 'drafts', 'starred', 'archive'];
  
  useEffect(() => {
    // Check if user is authenticated as admin
    const adminAuth = localStorage.getItem('isAdminAuthenticated');
    if (adminAuth !== 'true') {
      // Redirect to admin login if not authenticated
      navigate('/admin-login');
    } else {
      setIsAuthenticated(true);
    }
  }, [navigate]);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    setSelectedEmail(null);
  };

  const handleEmailClick = (email) => {
    // Mark as read if it was unread
    if (!email.read && email.folder === 'inbox') {
      const updatedEmails = emails.map(e => 
        e.id === email.id ? { ...e, read: true } : e
      );
      setEmails(updatedEmails);
    }
    setSelectedEmail(email);
    setComposeMode(false);
  };

  const handleBackToList = () => {
    setSelectedEmail(null);
    setComposeMode(false);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleComposeClick = () => {
    setSelectedEmail(null);
    setComposeMode(true);
    setNewEmail({
      to: '',
      subject: '',
      body: ''
    });
  };

  const handleNewEmailChange = (e) => {
    const { name, value } = e.target;
    setNewEmail(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSendEmail = () => {
    // In a real app, this would send the email to the server
    alert('Email sent successfully!');
    setComposeMode(false);
  };

  const handleSaveDraft = () => {
    // In a real app, this would save the draft to the server
    alert('Draft saved successfully!');
    setComposeMode(false);
  };

  const handleToggleStar = (emailId) => {
    const updatedEmails = emails.map(email => 
      email.id === emailId ? { ...email, starred: !email.starred } : email
    );
    setEmails(updatedEmails);
  };

  const handleMarkAsRead = (emailId, read = true) => {
    const updatedEmails = emails.map(email => 
      email.id === emailId ? { ...email, read } : email
    );
    setEmails(updatedEmails);
  };

  const handleDeleteEmail = (emailId) => {
    // In a real app, this would move the email to trash or delete it
    const updatedEmails = emails.filter(email => email.id !== emailId);
    setEmails(updatedEmails);
    if (selectedEmail && selectedEmail.id === emailId) {
      setSelectedEmail(null);
    }
  };

  // Filter emails based on current tab and search term
  const filteredEmails = emails.filter(email => {
    const matchesFolder = currentTab === 3 
      ? email.starred 
      : (currentTab === 4 ? email.folder === 'archive' : email.folder === tabFolders[currentTab]);
    
    const matchesSearch = searchTerm === '' || 
      email.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
      email.sender.toLowerCase().includes(searchTerm.toLowerCase()) ||
      email.body.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesFolder && matchesSearch;
  });

  if (!isAuthenticated) {
    return null; // Don't render anything while checking authentication
  }

  return (
    <Box sx={{ backgroundColor: '#fbfbfb', minHeight: '100vh' }}>
      <AppBar position="static" sx={{ backgroundColor: '#0a233b' }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            component={Link}
            to="/dashboard"
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Email Center - info@lantenapm.com
          </Typography>
          <TextField
            placeholder="Search emails..."
            size="small"
            variant="outlined"
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              borderRadius: 1,
              width: 250,
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: 'transparent',
                },
                '&:hover fieldset': {
                  borderColor: 'transparent',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'transparent',
                },
              },
              '& .MuiInputBase-input': {
                color: 'white',
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'white' }} />
                </InputAdornment>
              ),
            }}
          />
          <IconButton color="inherit" onClick={handleMenuOpen}>
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={handleMenuClose}>Settings</MenuItem>
            <MenuItem onClick={() => { handleMenuClose(); navigate('/dashboard'); }}>Back to Dashboard</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: 3, mb: 5 }}>
        <Grid container spacing={2}>
          {/* Left Sidebar */}
          <Grid item xs={12} md={3} lg={2}>
            <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
              <Button
                variant="contained"
                fullWidth
                startIcon={<AddIcon />}
                onClick={handleComposeClick}
                sx={{
                  backgroundColor: '#f66b10',
                  '&:hover': { backgroundColor: '#e05a00' },
                  mb: 3
                }}
              >
                Compose
              </Button>
              
              <Tabs
                orientation="vertical"
                value={currentTab}
                onChange={handleTabChange}
                sx={{
                  '& .MuiTab-root': {
                    alignItems: 'flex-start',
                    textAlign: 'left',
                    pl: 0
                  }
                }}
              >
                <Tab 
                  icon={<InboxIcon />} 
                  iconPosition="start" 
                  label="Inbox" 
                  sx={{ 
                    color: currentTab === 0 ? '#f66b10' : 'inherit',
                    fontWeight: currentTab === 0 ? 'bold' : 'normal'
                  }} 
                />
                <Tab 
                  icon={<SendIcon />} 
                  iconPosition="start" 
                  label="Sent" 
                  sx={{ 
                    color: currentTab === 1 ? '#f66b10' : 'inherit',
                    fontWeight: currentTab === 1 ? 'bold' : 'normal'
                  }} 
                />
                <Tab 
                  icon={<DraftsIcon />} 
                  iconPosition="start" 
                  label="Drafts" 
                  sx={{ 
                    color: currentTab === 2 ? '#f66b10' : 'inherit',
                    fontWeight: currentTab === 2 ? 'bold' : 'normal'
                  }} 
                />
                <Tab 
                  icon={<StarIcon />} 
                  iconPosition="start" 
                  label="Starred" 
                  sx={{ 
                    color: currentTab === 3 ? '#f66b10' : 'inherit',
                    fontWeight: currentTab === 3 ? 'bold' : 'normal'
                  }} 
                />
                <Tab 
                  icon={<ArchiveIcon />} 
                  iconPosition="start" 
                  label="Archive" 
                  sx={{ 
                    color: currentTab === 4 ? '#f66b10' : 'inherit',
                    fontWeight: currentTab === 4 ? 'bold' : 'normal'
                  }} 
                />
              </Tabs>
            </Paper>
          </Grid>
          
          {/* Email List and Content */}
          <Grid item xs={12} md={9} lg={10}>
            <Paper elevation={2} sx={{ borderRadius: 2, minHeight: '70vh' }}>
              {!selectedEmail && !composeMode ? (
                // Email List
                <Box>
                  <Box sx={{ 
                    p: 2, 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    borderBottom: '1px solid #e0e0e0'
                  }}>
                    <Typography variant="h6" sx={{ color: '#0a233b' }}>
                      {currentTab === 0 && 'Inbox'}
                      {currentTab === 1 && 'Sent'}
                      {currentTab === 2 && 'Drafts'}
                      {currentTab === 3 && 'Starred'}
                      {currentTab === 4 && 'Archive'}
                    </Typography>
                    <IconButton size="small" sx={{ color: '#0a233b' }}>
                      <RefreshIcon />
                    </IconButton>
                  </Box>
                  
                  <List sx={{ p: 0 }}>
                    {filteredEmails.length > 0 ? (
                      filteredEmails.map((email) => (
                        <ListItem 
                          key={email.id}
                          sx={{ 
                            p: 2,
                            borderBottom: '1px solid #e0e0e0',
                            backgroundColor: email.read ? 'inherit' : '#fff4e5',
                            '&:hover': { backgroundColor: '#f5f5f5' },
                            cursor: 'pointer'
                          }}
                          onClick={() => handleEmailClick(email)}
                        >
                          <Box sx={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                            <Box sx={{ mr: 1 }}>
                              <IconButton 
                                size="small" 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleStar(email.id);
                                }}
                                sx={{ color: email.starred ? '#f66b10' : 'inherit' }}
                              >
                                {email.starred ? <StarIcon /> : <StarBorderIcon />}
                              </IconButton>
                            </Box>
                            
                            <Box sx={{ flexGrow: 1 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography 
                                  variant="subtitle1" 
                                  sx={{ 
                                    fontWeight: email.read ? 'normal' : 'bold',
                                    color: '#0a233b'
                                  }}
                                >
                                  {email.folder === 'sent' || email.folder === 'drafts' 
                                    ? `To: ${email.sender}` 
                                    : email.sender}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {email.date} {email.time}
                                </Typography>
                              </Box>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontWeight: email.read ? 'normal' : 'bold',
                                  mb: 0.5
                                }}
                              >
                                {email.subject}
                              </Typography>
                              <Typography 
                                variant="body2" 
                                color="text.secondary" 
                                noWrap
                                sx={{ maxWidth: '80%' }}
                              >
                                {email.body.substring(0, 100)}...
                              </Typography>
                            </Box>
                            
                            <Box>
                              <IconButton 
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (email.read) {
                                    handleMarkAsRead(email.id, false);
                                  } else {
                                    handleMarkAsRead(email.id, true);
                                  }
                                }}
                                sx={{ color: '#0a233b' }}
                              >
                                {email.read ? <MarkEmailUnreadIcon /> : <MarkEmailReadIcon />}
                              </IconButton>
                              <IconButton 
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteEmail(email.id);
                                }}
                                sx={{ color: '#0a233b' }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          </Box>
                        </ListItem>
                      ))
                    ) : (
                      <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="body1" color="text.secondary">
                          No emails found
                        </Typography>
                      </Box>
                    )}
                  </List>
                </Box>
              ) : selectedEmail ? (
                // Email Detail View
                <Box>
                  <Box sx={{ 
                    p: 2, 
                    display: 'flex', 
                    alignItems: 'center',
                    borderBottom: '1px solid #e0e0e0'
                  }}>
                    <IconButton 
                      edge="start" 
                      onClick={handleBackToList}
                      sx={{ color: '#0a233b', mr: 1 }}
                    >
                      <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h6" sx={{ flexGrow: 1, color: '#0a233b' }}>
                      {selectedEmail.subject}
                    </Typography>
                    <IconButton 
                      onClick={() => handleToggleStar(selectedEmail.id)}
                      sx={{ color: selectedEmail.starred ? '#f66b10' : 'inherit' }}
                    >
                      {selectedEmail.starred ? <StarIcon /> : <StarBorderIcon />}
                    </IconButton>
                    <IconButton 
                      onClick={() => handleDeleteEmail(selectedEmail.id)}
                      sx={{ color: '#0a233b' }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                  
                  <Box sx={{ p: 3 }}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      mb: 3
                    }}>
                      <Avatar 
                        sx={{ 
                          bgcolor: '#0a233b', 
                          width: 50, 
                          height: 50,
                          mr: 2
                        }}
                      >
                        {selectedEmail.sender.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                          {selectedEmail.folder === 'sent' || selectedEmail.folder === 'drafts' 
                            ? `To: ${selectedEmail.sender}` 
                            : selectedEmail.sender}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {selectedEmail.folder === 'sent' || selectedEmail.folder === 'drafts' 
                            ? selectedEmail.sender 
                            : selectedEmail.email}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {selectedEmail.date} at {selectedEmail.time}
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Divider sx={{ mb: 3 }} />
                    
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        whiteSpace: 'pre-line',
                        lineHeight: 1.7
                      }}
                    >
                      {selectedEmail.body}
                    </Typography>
                    
                    <Box sx={{ mt: 4 }}>
                      <Button
                        variant="contained"
                        startIcon={<SendIcon />}
                        sx={{
                          backgroundColor: '#0a233b',
                          '&:hover': { backgroundColor: '#0a233b' },
                          mr: 2
                        }}
                        onClick={handleComposeClick}
                      >
                        Reply
                      </Button>
                      <Button
                        variant="outlined"
                        sx={{
                          borderColor: '#a6d1ea',
                          color: '#0a233b',
                          '&:hover': {
                            borderColor: '#0a233b',
                            backgroundColor: 'rgba(166, 209, 234, 0.1)'
                          }
                        }}
                        onClick={handleComposeClick}
                      >
                        Forward
                      </Button>
                    </Box>
                  </Box>
                </Box>
              ) : (
                // Compose Email View
                <Box>
                  <Box sx={{ 
                    p: 2, 
                    display: 'flex', 
                    alignItems: 'center',
                    borderBottom: '1px solid #e0e0e0'
                  }}>
                    <IconButton 
                      edge="start" 
                      onClick={handleBackToList}
                      sx={{ color: '#0a233b', mr: 1 }}
                    >
                      <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h6" sx={{ flexGrow: 1, color: '#0a233b' }}>
                      Compose New Email
                    </Typography>
                  </Box>
                  
                  <Box sx={{ p: 3 }}>
                    <TextField
                      fullWidth
                      label="To"
                      name="to"
                      value={newEmail.to}
                      onChange={handleNewEmailChange}
                      margin="normal"
                      variant="outlined"
                      sx={{ mb: 2 }}
                    />
                    
                    <TextField
                      fullWidth
                      label="Subject"
                      name="subject"
                      value={newEmail.subject}
                      onChange={handleNewEmailChange}
                      margin="normal"
                      variant="outlined"
                      sx={{ mb: 2 }}
                    />
                    
                    <TextField
                      fullWidth
                      label="Message"
                      name="body"
                      value={newEmail.body}
                      onChange={handleNewEmailChange}
                      margin="normal"
                      variant="outlined"
                      multiline
                      rows={12}
                      sx={{ mb: 3 }}
                    />
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Box>
                        <Button
                          variant="contained"
                          startIcon={<SendIcon />}
                          onClick={handleSendEmail}
                          sx={{
                            backgroundColor: '#f66b10',
                            '&:hover': { backgroundColor: '#e05a00' },
                            mr: 2
                          }}
                        >
                          Send
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={<DraftsIcon />}
                          onClick={handleSaveDraft}
                          sx={{
                            borderColor: '#a6d1ea',
                            color: '#0a233b',
                            '&:hover': {
                              borderColor: '#0a233b',
                              backgroundColor: 'rgba(166, 209, 234, 0.1)'
                            }
                          }}
                        >
                          Save as Draft
                        </Button>
                      </Box>
                      <Button
                        variant="text"
                        onClick={handleBackToList}
                        sx={{ color: '#0a233b' }}
                      >
                        Discard
                      </Button>
                    </Box>
                  </Box>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default AdminEmails;
